import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "@runcheetah/ui";
import {
  deriveEditableAudienceCards,
  deriveEditableContentDnaAngles,
} from "@/lib/brand-onboarding-display";

import { AudienceVoiceAnglesStep } from "./audience-voice-angles-step";
import type {
  BrandProfile,
  BrandVisualStyle,
  BrandVoice,
  ConfirmReviewStep,
  EditableBrandFields,
} from "./confirm-review-data";
import { deriveValueProps, VISUAL_FIELDS } from "./confirm-review-data";
import { RevealSlot } from "./reveal-slot";
import { ValuePropsStep } from "./value-props-step";
import { VisualIdentityStep, type PrimaryOnboardingLogo } from "./visual-identity-step";

const STEP_COPY: Record<
  ConfirmReviewStep,
  {
    eyebrowId: string;
    eyebrow: string;
    headId: string;
    head: string;
    tailId: string;
    tail: string;
    subId: string;
    sub: string;
  }
> = {
  0: {
    eyebrowId: "brandOnboarding.confirm.visualStepEyebrow",
    eyebrow: "Step 1 of 3 · Visual identity",
    headId: "brandOnboarding.confirm.visualStepHead",
    head: "Let's confirm your",
    tailId: "brandOnboarding.confirm.visualStepTail",
    tail: "logo and colors.",
    subId: "brandOnboarding.confirm.visualStepSub",
    sub: "We pulled these from your site. Adjust anything that doesn't feel right.",
  },
  1: {
    eyebrowId: "brandOnboarding.confirm.valueStepEyebrow",
    eyebrow: "Step 2 of 3 · What you do",
    headId: "brandOnboarding.confirm.valueStepHead",
    head: "And the",
    tailId: "brandOnboarding.confirm.valueStepTail",
    tail: "value your brand delivers.",
    subId: "brandOnboarding.confirm.valueStepSub",
    sub: "Start with the primary USP, then add the supporting reasons that make it believable.",
  },
  2: {
    eyebrowId: "brandOnboarding.confirm.essenceStepEyebrow",
    eyebrow: "Step 3 of 3 · Brand essence",
    headId: "brandOnboarding.confirm.essenceStepHead",
    head: "Finally, who you",
    tailId: "brandOnboarding.confirm.essenceStepTail",
    tail: "write for and how.",
    subId: "brandOnboarding.confirm.essenceStepSub",
    sub: "Tune your audience, voice, and content angles before we draft.",
  },
};

export function ConfirmReviewFlow({
  sourceDomain,
  profile,
  voice,
  visual,
  primaryLogo,
  allRevealed,
  mutationPending,
  logoUploadPending,
  uploadingLogoName,
  onSave,
  onUploadLogo,
  onContinue,
}: {
  sourceDomain: string;
  profile: BrandProfile;
  voice: BrandVoice;
  visual: BrandVisualStyle;
  primaryLogo: PrimaryOnboardingLogo | null;
  allRevealed: boolean;
  mutationPending: boolean;
  logoUploadPending: boolean;
  uploadingLogoName: string | null;
  onSave: (fields: EditableBrandFields) => Promise<void> | void;
  onUploadLogo: (file: File) => Promise<void> | void;
  onContinue: () => void;
}) {
  const intl = useIntl();
  const [confirmStep, setConfirmStep] = useState<ConfirmReviewStep>(0);
  const valueProps = useMemo(() => deriveValueProps({ profile }), [profile]);
  const audienceSegments = deriveEditableAudienceCards(profile);
  const contentAngles = deriveEditableContentDnaAngles(voice);
  const cur = STEP_COPY[confirmStep];
  const isLastStep = confirmStep === 2;

  const advance = () => {
    if (isLastStep) {
      onContinue();
      return;
    }
    setConfirmStep((step) => Math.min(2, step + 1) as ConfirmReviewStep);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35 }}
      className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8"
      data-testid="stage-confirm"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-96">
          <div
            data-testid="confirm-step-eyebrow"
            className="mb-2 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
          >
            <Sparkles className="h-3 w-3 text-primary/70 motion-safe:animate-pulse" aria-hidden />
            {intl.formatMessage({ id: cur.eyebrowId, defaultMessage: cur.eyebrow })}
          </div>
          <h1 className="max-w-3xl text-balance text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
            {intl.formatMessage({ id: cur.headId, defaultMessage: cur.head })}{" "}
            <em className="font-serif font-normal italic text-foreground/60">
              {intl.formatMessage({ id: cur.tailId, defaultMessage: cur.tail })}
            </em>
          </h1>
          <p
            className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground"
            data-testid="confirm-subtitle"
          >
            {intl.formatMessage({ id: cur.subId, defaultMessage: cur.sub })}
          </p>
        </div>
      </div>

      <div className="min-h-[280px]">
        {confirmStep === 0 ? (
          <RevealSlot revealed={allRevealed} skeletonHeight={172} testId="skeleton-visual">
            <VisualIdentityStep
              sourceDomain={sourceDomain}
              profile={profile}
              visual={visual}
              primaryLogo={primaryLogo}
              uploadPending={logoUploadPending}
              uploadingLogoName={uploadingLogoName}
              onSaveColors={(next) => onSave({ [VISUAL_FIELDS.colorPalette]: next })}
              onUploadLogo={onUploadLogo}
            />
          </RevealSlot>
        ) : null}
        {confirmStep === 1 ? (
          <RevealSlot revealed={allRevealed} skeletonHeight={180} testId="skeleton-what-you-do">
            <ValuePropsStep valueProps={valueProps} onSave={onSave} />
          </RevealSlot>
        ) : null}
        {confirmStep === 2 ? (
          <RevealSlot revealed={allRevealed} skeletonHeight={420} testId="skeleton-audience">
            <AudienceVoiceAnglesStep
              audienceSegments={audienceSegments}
              voice={voice}
              contentAngles={contentAngles}
              onSave={onSave}
            />
          </RevealSlot>
        ) : null}
      </div>

      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          className="rounded-full"
          onClick={() => setConfirmStep((step) => Math.max(0, step - 1) as ConfirmReviewStep)}
          disabled={confirmStep === 0}
          data-testid="button-confirm-back"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <FormattedMessage id="brandOnboarding.confirm.back" defaultMessage="Back" />
        </Button>

        <div className="inline-flex items-center gap-4">
          <div
            aria-hidden
            data-testid="confirm-step-dots"
            className="inline-flex items-center gap-1.5"
          >
            {([0, 1, 2] as const).map((step) => (
              <span
                key={step}
                className={`h-1.5 rounded-full transition-[width,background-color] ${
                  step === confirmStep ? "w-5 bg-foreground" : "w-1.5 bg-foreground/20"
                }`}
              />
            ))}
          </div>
          <Button
            type="button"
            onClick={advance}
            disabled={!allRevealed || mutationPending}
            data-testid="button-confirm"
            className="rounded-full px-5"
          >
            {isLastStep ? (
              <FormattedMessage
                id="brandOnboarding.confirm.generateFirstPosts"
                defaultMessage="Generate first posts"
              />
            ) : (
              <FormattedMessage id="brandOnboarding.confirm.continue" defaultMessage="Continue" />
            )}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
